import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { appRegexs, appStrings, domianTableLookupIdAssignedToWildfire, domianTableLookupIdCostCenter, domianTableLookupIdCostingActivity, domianTableLookupIdCostRateType, domianTableLookupIdEquipmentSpecifications, domianTableLookupIdForestrArea, domianTableLookupIdFuelProvidedByGoa, domianTableLookupIdUnitType, domianTableLookupIdWorkPerformed, LodgingCostRateType, MealCostRateType, RatesFromFires, TravelCostRateType, tz } from "../common/constants";
import { wdpUtil } from "../common/utils/util";
import { appLog } from "../common/utils/app-log";
import { CostRateTypeFromDomain,  GenListType, LookupRelationshipByKey, LookupRelations, RelationElement } from "../common/types";
import { BasicInfo_ui,  FireEquipmentInfo_raw, Rate_raw, TicketInfo_ui } from "../common/models/TicketInfo";


dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(tz);

export default class TicketService {


  /******************************************************************************************
   * Returns true when internal order follows the fire-number derived pattern:
   * FF{identifier}{YY}, e.g. SWF001 - 2025 -> FFSWF00125
  *******************************************************************************************/
  static isInternalOrderMatchingFireNo(fireNo: GenListType | string | null, internalOrder: GenListType | string | null): boolean {
    const fireNoLabel = wdpUtil.getFormFieldVal(fireNo, true);
    const internalOrderVal = wdpUtil.getFormFieldVal(internalOrder);

    if (wdpUtil.isNullOrEmpty(fireNoLabel) || wdpUtil.isNullOrEmpty(internalOrderVal)) {
      return false;
    }

    const parts = fireNoLabel.split('-');
    if (!parts || parts.length !== 2) {
      return false;
    }

    const identifier = parts[0].trim();
    const year = parts[1].trim();
    const mustHave = `FF${identifier}${year.substring(2)}`;

    return internalOrderVal.includes(mustHave);
  }

  /******************************************************************************************
   * Returns true for cost summary rows that are managed by section components
   * (travel, meal, lodging, work performed) and should not be directly edited/deleted in
   * table controls. Work-performed rows are identified by workPerformedType rather than
   * rateType, since a work row's rate type (Standard/Standby/etc) isn't unique to that section.
  *******************************************************************************************/
  static isManagedCostSummaryRateType(rateType: GenListType | string | null | undefined, workPerformedType?: GenListType | string | null): boolean {
    if (!wdpUtil.isNullOrEmpty(wdpUtil.getFormFieldVal(workPerformedType as string | GenListType | null, true))) {
      return true;
    }

    const rateLabel = wdpUtil.getFormFieldVal(rateType as string | GenListType | null, true);
    if (wdpUtil.isNullOrEmpty(rateLabel)) {
      return false;
    }

    const managedRateTypes = [...TravelCostRateType, ...MealCostRateType, ...LodgingCostRateType];
    return wdpUtil.inListCI(rateLabel, managedRateTypes);
  }




  /******************************************************************************************
   *
   * @returns
  *******************************************************************************************/
  static validateProp(who: string, value: string | GenListType | null, _extraParam?: any): string {
    let err = "";
    let emptyFld = wdpUtil.isNullorUndefined(value);


    switch (who) {
      //-----------------------------------
      case "ticketNumber": {
        // val here is string
        err = emptyFld ? "Cannot be empty. Enter a ticket number." : appRegexs.ticketNumber.test(value as string || '') == false ? "Enter a valid ticket number (letters and numbers only)." : "";
      }
        break;
      case "date": {
        // val here is string
        if (emptyFld) {
          err = "Cannot be empty. Enter a date.";
        }
        else {
          if (appRegexs.date.test(value as string || '') == false) {
            err = "Ticket a valide date (YYYY-MM-DD).";
          }
          else {
            const todayDt = new Date(); todayDt?.setHours(0); todayDt?.setMinutes(0); todayDt?.setSeconds(0); todayDt?.setMilliseconds(0);
            const valDt = new Date(value as string);
            if (valDt > todayDt) {
              err = 'Date cannot be in the future.';
            }

          }
        }

      }
        break;
      case "contractNumber": {
        // val here is GenTypeList
        err = emptyFld ? "Select a valid Contract number." : "";
      }
        break;

      case "contractorName": {
        // val here is string
        err = emptyFld ? "Cannot be empty. Enter contractor name." : "";
      }
        break;
      case "forestAreaId": {
        // val here is GenTypeList
        err = emptyFld ? "Select a valid Forest area." : "";
      }
        break;
      case "standbyBase": {
        err = emptyFld ? "Cannot be empty. Enter standby base." : "";
      }
      break;
      case "fieldSupervisor": {
        // Optional field - only validate max length if provided
        if (!emptyFld && (value as string).length > 120) {
          err = "Field supervisor cannot exceed 120 characters.";
        }
      }
      break;
      case "unitNumber": {
        // val here is GenTypeList
        err = emptyFld ? "Select a valid Unit number." : "";
      }
        break;

      case "serialNumber": {
        // val here is GenTypeList
        err = emptyFld ? "Select a Serial number" : "";
      }
        break;

      case "equipment": {
        // val here is GenTypeList
        err = emptyFld ? "Select an Equipment type and sub-type." : "";
      }
        break;

      /////////////////////////////////// Cost Summary Fields
      case "fireNumber": {
        // val here is GenTypeList
        err = emptyFld ? "Select a valid Fire number." : "";
      }
        break;
      case "costActivityCode": {
        // val here is GenTypeList
        err = emptyFld ? "Select a Cost activity code." : "";
      }
        break;
      case "rateType": {
        // val here is GenTypeList
        err = emptyFld ? "Select a Rate type." : "";
      }
        break;
      case "noOfUnits": {
        // val here is string
        err = emptyFld ? "Cannot be empty. Enter a number of units." : "";
      }
        break;
      case "rateUnit": {
        // val here is GenTypeList
        err = emptyFld ? "Cannot be empty. Enter a rate unit." : "";
      }
        break;
      case "ratePerUnit": {
        // val here is string
        err = emptyFld ? "Cannot be empty. Enter a Rate per unit." : "";
      }
        break;
      case "amount": {
        // val here is string
        err = emptyFld ? "Cannot be empty" : "";
      }
        break;
      case "internalOrder": {
        // val here is GenTypeList
        err = emptyFld ? "Cannot be empty. Enter an Internal order code." : "";
        if(err=="" && _extraParam?.fireNumber){
          // check if the internal order match the fireNumber
          const parts = _extraParam?.fireNumber.split("-");
          if(parts && parts.length===2){
            const identifier  = parts[0].trim();
            const year        = parts[1].trim();
            
            const mustHave = `FF${identifier}${year.substring(2)}`;
            err = (value as string).includes(mustHave)?"":appStrings.internalOrderNotMatchFireNo;
            
            //const qStr= `FF${identifier}${year.substring(2)}`;
          }
        }
      }
      break;
      case "costCenter": {
        // val here is GenTypeList
        err = emptyFld ? "Cannot be empty. Enter a cost center." : "";
      }
        break;
      case "fund": {
        // val here is string
        err = emptyFld ? "Cannot be empty" : "";
      }
        break;
      case "account": {
        // val here is string
        err = emptyFld ? "Cannot be empty" : "";
      }
        break;

    } //switch
    return err;
  }

  /******************************************************************************************
   *
   * @returns
  *******************************************************************************************/
  static getEquipmentRaw(equFromFires: FireEquipmentInfo_raw[], equpInfo: BasicInfo_ui) {
    const equRaw = (equFromFires).find((e: FireEquipmentInfo_raw) =>
      wdpUtil.strEqual(e.contractNumber, equpInfo.contractNumber!.value) &&
      wdpUtil.strEqual(e.unitNumber, equpInfo.unitNumber!.value) &&
      wdpUtil.strEqual(e.equipmentId, equpInfo.equipment!.value)
    );
    return equRaw;
  }

  /******************************************************************************************
   * from Fires and from Domian ..based on the rate type
   * we have array of rate types for each source
  *******************************************************************************************/
  static extractAddOnsFromFires(rawJsonFromFires: FireEquipmentInfo_raw[], equpInfo?: BasicInfo_ui | null): GenListType[] {
    if (equpInfo != null) {
      const myContract = wdpUtil.getFormFieldVal(equpInfo.contractNumber);
      const myUnit = wdpUtil.getFormFieldVal(equpInfo.unitNumber);
      //const myEqu = wdpUtil.geValFromFld(equpInfo.equipment);
      const mySerial = wdpUtil.getFormFieldVal(equpInfo.serialNumber);

      // expecting 1 element
      const strippedDown = rawJsonFromFires.filter((e: FireEquipmentInfo_raw) =>
        e.contractNumber === myContract &&
        e.unitNumber === myUnit &&
        e.serialNumber === mySerial
      );

      if (strippedDown != null && strippedDown.length > 0) {
        const foundRec = strippedDown[0];
        const addons_raw = foundRec.addOns;
        // NOTE: there might be an add ones with descrion like ''  ..just empty.
        // this one means no AdOns and No Rate.. we should populate all the avialbe units in domain and set the Rate per uint to 0.00
        // there is another posible option named 'Without add ons'. this one has a rate in FIRES and we need to find it . the selectedAddOns in that rate will be null
        const addOns_GoodOptsList = addons_raw
          .filter((a: any) => a.description != "")
          .map((a: any) => ({ label: a.description, value: a.description }));

        //const addOns_GoodOptsList = addons_raw.map((a: any) => ({ label: a.description, value: a.description }));
        // skip the empty addOns ,,we dont want to show empty in the list.
        // instead, user can clear the field if he want no rate , becuse

        return addOns_GoodOptsList;
      }
    }

    return [];
  }
  /******************************************************************************************
   *
   * @returns
  *******************************************************************************************/
  static getUnits4RateTyle(lookups: any, equFromFires: FireEquipmentInfo_raw[], myRateType: GenListType, equpInfo: BasicInfo_ui | undefined | null): GenListType[] {
    const ret: GenListType[] = [];

    // d0 we have contract no ? and do we row in the cost summart table
    // safeguard
    if (wdpUtil.isNullorUndefined(equpInfo) || wdpUtil.isNullorUndefined(equpInfo!.contractNumber) || wdpUtil.isNullorUndefined(equpInfo!.unitNumber) || wdpUtil.isNullorUndefined(equpInfo!.equipment) || wdpUtil.isNullorUndefined(myRateType)) {
      return [];
    }
    if (wdpUtil.isNullorUndefined(myRateType)) {
      return [];
    }

    // lookups  or FIRES 
    if (wdpUtil.inListCI(myRateType.label, RatesFromFires)) {
      const equRaw = TicketService.getEquipmentRaw(equFromFires, equpInfo!);

      if (equRaw != null) {
        if(equpInfo?.addOns === null || wdpUtil.isEmptyGenListType(equpInfo?.addOns)){
          // user cleaed the addOns ..meaning Case#4
          // if (lookups != null && lookups.genericRelationsList != null) {
          //   const found = TicketService.extractGoodUnitsForRateType(lookups.genericRelationsList, null);
          //   const unique = wdpUtil.getUniqueByProperty(found, "lookupId");
          //   return unique.map((a: RelationElement) => ({ label: a.lookupLabel.toLowerCase(), value: a.lookupLabel.toLowerCase() }));
          // }    
          return lookups.costRateUnitList;

        }else{
          // find the rate for the given rate type and rate unit and AddOns (if any)
          const found = equRaw.rates.filter((a: Rate_raw) => (wdpUtil.strEqual(a.rateTypeId, myRateType.value)));
          const unique = wdpUtil.getUniqueByProperty(found, "rateUnitId");
          return unique.map((a: Rate_raw) => ({ label: a.rateUnit.toLowerCase(), value: a.rateUnit.toLowerCase() }));
        }
      }
    }
    else {
      //NOTE:  thoese we got the rate from lookup not from FIRE..
      // and it is called TimeTrakingCost.
      if (lookups != null && lookups.genericRelationsList != null) {
        const found = TicketService.extractGoodUnitsForRateType(lookups.genericRelationsList, myRateType.value);
        const unique = wdpUtil.getUniqueByProperty(found, "lookupId");
        //return unique.map((a: RelationElement) => ({ label: a.lookupLabel.toLowerCase(), value: a.lookupId.toString().toLowerCase() }));
        return unique.map((a: RelationElement) => ({ label: a.lookupLabel.toLowerCase(), value: a.lookupLabel.toLowerCase() }));

      }

    }
    return [];
  }

  /******************************************************************************************
   *
   * @returns
   * from FIRES endpont call response , we have (description and selectedAddOns)
    
    when description= ""                  => user must enter the rate manully , I wil set it 0.00
    when description= "Without add ons"   => selectedAddOns = 'Without add ons'; rate corresponding rate type and rate unit selected
    when description= "Thumb %"           => selectedAddOns = ''Thumb %"; rate corresponding rate type and rate unit selected (edited)
  *******************************************************************************************/
  static getRate4RateTyle(lookups: any, equFromFires: FireEquipmentInfo_raw[], myRateType: GenListType, myRateUnit: GenListType, equpInfo: BasicInfo_ui | undefined | null) {

    if (wdpUtil.isNullorUndefined(myRateType)) {
      return "0.00";
    }

    if (wdpUtil.inListCI(myRateType.label, RatesFromFires)) {
      
      // if equpInfo.addOns is null ,meaning the user cleared the dropwdown with X. we should return 
      if (wdpUtil.isNullorUndefined(equpInfo!.addOns)) { 
        return "0.00";
      }

      if(equpInfo!=null && equpInfo!.contractNumber!=null && equpInfo!.unitNumber!=null &&  equpInfo!.equipment!=null && myRateUnit!=null) {
        const equRaw = TicketService.getEquipmentRaw(equFromFires, equpInfo!);
        if (equRaw != null) {
          // find the rate for the given rate type and rate unit and AddOns (if any)
          // NOTE:selectedAddOns might be null or a string,  if null we need to treat it as ""
          const rates_trans = equRaw.rates;//.map((a: Rate_raw) => ({...a,selectedAddOns:a.selectedAddOns!=null?a.selectedAddOns:""}));
          
          // ready to find the target..find will return undefined if not found
          const found = rates_trans.find((a: Rate_raw) => (
            ( wdpUtil.strEqual(a.rateTypeId, myRateType.value)) &&
            ( wdpUtil.strEqual(a.rateUnit, myRateUnit.label)) &&
            ( wdpUtil.strEqual(a.selectedAddOns,equpInfo!.addOns!.value) ))); 
            
            return (found!=null && found.ratePerUnit != null) ? found.ratePerUnit.toString() : '';
        }
      }
    }
    else {
      // NOTE:  thoese we got the rate from lookup not from FIRE..
      // and it is called TimeTrakingCost.
      const _ratePerUnit = lookups?.costRateTypeList.find((e: CostRateTypeFromDomain) => e.LookupId.toString() === myRateType.value)?.TimeTrakingCost;
      return _ratePerUnit;
    }

    return '';
  }
  /******************************************************************************************
   * Get available Fuel Surcharge rates as a list for the selected equipment specification.
  *******************************************************************************************/
  static getRate4FuelSurcharge(lookups: any, equFromFires: FireEquipmentInfo_raw[], equpInfo: BasicInfo_ui | undefined | null, extra?: any): GenListType[] {
    if (!equpInfo?.contractNumber || !equpInfo?.unitNumber || !equpInfo?.equipment) {
      return [];
    }

    const equRaw = TicketService.getEquipmentRaw(equFromFires, equpInfo);
    const equSpecificationId = equRaw?.specificationId ? equRaw.specificationId.toString() : null;

    return TicketService.extractFromRelations(lookups.genericRelationsList, "ratePerUnit4FuelSurcharge", {
      rateTypeId: "Fuel Surcharge",
      EquSpecificationId: equSpecificationId,
      value: extra?.value,
    });
  }
  /******************************************************************************************
   *
   * @returns
   * extract the units for the given rateType OR return al if rate type is null
  *******************************************************************************************/
  static extractGoodUnitsForRateType(data: LookupRelations[], rateTypeId: string|null): RelationElement[] {
    const results: RelationElement[] = [];
    for (const item of data) {

      const rateType = item.itemElements.find((e: RelationElement) => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdCostRateType));
      if (!rateType)
        continue;

      if( (rateTypeId!=null && wdpUtil.strEqual(rateType.lookupId, rateTypeId)) || rateTypeId==null ){
        const rateUnit = item.itemElements.find((e: RelationElement) => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdUnitType));
        if (rateUnit) {
          results.push(rateUnit);
        }
      }
    }
    return results;
  }
  /******************************************************************************************
   *
   * @returns
  *******************************************************************************************/
  static getCostActivity4RateTypeId(lookups: any, rateTypeId: string | null) {
    let activityOpsList: GenListType[] = [];
    if (!wdpUtil.isNullorUndefined(lookups.genericRelationsList) && rateTypeId) {

      const itemElements      = lookups.genericRelationsList.map((e: LookupRelations) => e.itemElements);
      const withRateType      = itemElements?.filter((e: RelationElement[]) => e.find((n: RelationElement)  => wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdCostRateType) && wdpUtil.strEqual(n.lookupId, rateTypeId)));
      const withActivityCode  = withRateType?.map((e: RelationElement[]) => e.filter((n: RelationElement)   => wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdCostingActivity)))?.flat(1);

      if (withActivityCode) {

        const withActivityCodeSorted = withActivityCode.sort((a: RelationElement, b: RelationElement) => a.code?.toLowerCase()?.localeCompare(b.code?.toLowerCase() ?? ''));
        activityOpsList = withActivityCodeSorted.map((e: RelationElement) => { return ({ label: `${e.code} - ${e.lookupLabel}`, value: e.lookupId.toString() }) });


      }
    }
    return activityOpsList;
  }
  /******************************************************************************************
   *
   * @returns
   * 
   * 621781 WFM HQ Operations Heavy Equipment P&R      Edmonton
    621807 High Level Heavy Equipment P&R             High Level
    621826 Peace River Heavy Equipment P&R            Peace River
    621845 Grande Prairie Heavy Equipment P&R         Grande Prairie
    621864 Fort McMurray Heavy Equipment P&R          Fort McMurray
    621883 Slave Lake Heavy Equipment P&R             Slave Lake
    621902 Lac La Biche Heavy Equipment P&R           Lac La Biche 
    621921 Edson Heavy Equipment P&R                  Edson
    621940 Rocky Mnt House Heavy Equipment P&R        Rocky Mtn House
    621959 Whitecourt Heavy Equipment P&R             Whitecourt
    621978 Calgary Heavy Equipment P&R                Calgary
  *******************************************************************************************/
  static getCostCenter4ForestArea(lookups: any, forestArea: string | null = null):GenListType[] {
    const ForestAreaCostCenterMap: Record<string, { name: string; id: string }> = {
    "Edmonton":        { name: "WFM HQ Operations Heavy Equipment P&R", id: "621781" },
    "High Level":      { name: "High Level Heavy Equipment P&R",        id: "621807" },
    "Peace River":     { name: "Peace River Heavy Equipment P&R",       id: "621826" },
    "Grande Prairie":  { name: "Grande Prairie Heavy Equipment P&R",    id: "621845" },
    "Fort McMurray":   { name: "Fort McMurray Heavy Equipment P&R",     id: "621864" },
    "Slave Lake":      { name: "Slave Lake Heavy Equipment P&R",        id: "621883" },
    "Lac La Biche":    { name: "Lac La Biche Heavy Equipment P&R",      id: "621902" },
    "Edson":           { name: "Edson Heavy Equipment P&R",             id: "621921" },
    "Rocky Mtn House": { name: "Rocky Mnt House Heavy Equipment P&R",   id: "621940" },
    "Whitecourt":      { name: "Whitecourt Heavy Equipment P&R",        id: "621959" },
    "Calgary":         { name: "Calgary Heavy Equipment P&R",           id: "621978" }
    };

    if (!forestArea) return [];
    
    const key = forestArea.trim().toLowerCase();
    const match = Object.entries(ForestAreaCostCenterMap).find(([faName]) =>faName.toLowerCase().includes(key));
    if (!match) return [];
    const [, entry] = match;
    return[ {label: `${entry.name} - ${entry.id}`,value: entry.id}];

    // const relationShips: LookupRelations[] = lookups.genericRelationsList;
    // if (!wdpUtil.isNullorUndefined(relationShips)) {
    //   const itemElements = relationShips.map((e: LookupRelations) => e.itemElements);
      
    //   // Filter by forest area if provided, otherwise get all
    //   const withForestArea = forestArea
    //     ? itemElements?.filter((e: RelationElement[]) => 
    //         e.find((n: RelationElement) => 
    //           wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdForestrArea) && 
    //           wdpUtil.strEqual(n.lookupId, forestArea)
    //         )
    //       )
    //     : itemElements?.filter((e: RelationElement[]) => 
    //         e.find((n: RelationElement) => 
    //           wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdForestrArea)
    //         )
    //       );

    //   const costCenters = withForestArea
    //     ?.map((e: RelationElement[]) => 
    //       e.filter((n: RelationElement) => 
    //         wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdCostCenter)
    //       )
    //     )
    //     ?.flat(1);
        
    //   const asGenTypeList = costCenters
    //     .map((x: any) => ({
    //       label: `${x.lookupLabel} - ${x.lookupId.toString()}`,
    //       value: x.lookupId.toString()
    //     }))
    //     ?.sort((a: GenListType, b: GenListType) => a.label.localeCompare(b.label));
        
    //   return asGenTypeList;
    // }
    // return [];
  }

  // static getCostCenters(lookups: any) {
  //   return this.getCostCenter4ForestArea(lookups, null);
  // }
  /******************************************************************************************
   *
   * @returns this function to extract list or label for a given id
  *******************************************************************************************/
  static extractFromRelations(lookups: LookupRelations[], what: string,extra?:any):GenListType[] {
    if(wdpUtil.isNullorUndefined(lookups)|| lookups.length==0){
      return [];
    }
    const uniqueByValue = (list: GenListType[]): GenListType[] => {
      const valid = list.filter((x: GenListType) => !wdpUtil.isNullOrEmpty(x.value));
      return wdpUtil.getUniqueByProperty(valid, "value");
    };

    if (what === "forestArea") {
      const itemElements = lookups.map((e: LookupRelations) => e.itemElements)?.flat(1);
      const forestAreats = itemElements?.filter((e: RelationElement) =>  wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdForestrArea));
      
      let forestAreatsGenList = forestAreats.map((x:any) => ({label:x.lookupLabel,value:x.lookupId.toString()}))?.sort((a:GenListType,b:GenListType) => a.label.localeCompare(b.label));
      // get the ForestArea for a given id
      if(extra!=null && extra.value!=null){
        const found = forestAreatsGenList.find((x:GenListType) => wdpUtil.strEqual(x.value,extra.value));
        forestAreatsGenList=found!=null?[found]:[];
      }
      return forestAreatsGenList;
    }
    if (what === "costCenter") {
      const itemElements = lookups.map((e: LookupRelations) => e.itemElements)?.flat(1);
      const costCenters = itemElements?.filter((e: RelationElement)  => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdCostCenter));
      let costCentersGenList = costCenters.map((x:any) => ({label:x.lookupLabel,value:x.lookupId.toString()}))?.sort((a:GenListType,b:GenListType) => a.label.localeCompare(b.label));
      // get the costCenter for a given id
      if(extra!=null && extra.value!=null){
        const found = costCentersGenList.find((x:GenListType) => wdpUtil.strEqual(x.value,extra.value));
        costCentersGenList=found!=null?[found]:[];
      }
      return costCentersGenList;
    }
    if (what === "costActivityCode") {
      // const itemElements = lookups.map((e: LookupRelations) => e.itemElements)?.flat(1);
      // const activityCodes = itemElements?.filter((e: RelationElement)  => wdpUtil.strEqual(e.lookupTypeId, domianTableLookupIdCostingActivity));
      // let activityCodesGenList = activityCodes.map((x:any) => ({label:x.lookupLabel,value:x.lookupId.toString()}))?.sort((a:GenListType,b:GenListType) => a.label.localeCompare(b.label));
      // if(value!=null){
      //   const found = activityCodesGenList.find((x:GenListType) => wdpUtil.strEqual(x.value,value));
      //   activityCodesGenList=found!=null?[found]:[];
      // }
      // return activityCodesGenList;

      // WORK_PERFORMED combos define their own Rate Type + Costing Activity pairings, separate from the generic relationship, so search both.
      const workPerformedItemElements: RelationElement[][] = (extra?.workPerformedRelationsList as LookupRelationshipByKey[] ?? [])
        .flatMap(rel => rel.lookupRelationshipItems ?? [])
        .map(item => item.itemElements ?? []);
      const allItemElements = [...lookups.map((e: LookupRelations) => e.itemElements), ...workPerformedItemElements];

      let activityOpsList: GenListType[] = [];
      if (extra !=null && extra.rateTypeId) {

        const withRateType      = allItemElements?.filter((e: RelationElement[]) => e.find((n: RelationElement)  => wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdCostRateType) && wdpUtil.strEqual(n.lookupId, extra.rateTypeId)));
        const withActivityCode  = withRateType?.map((e: RelationElement[]) => e.filter((n: RelationElement)   => wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdCostingActivity)))?.flat(1);

        if (withActivityCode!=null) {
          const withActivityCodeSorted = withActivityCode.sort((a: RelationElement, b: RelationElement) => (a.code!=null && b.code!=null)? a.code.toLowerCase().localeCompare(b.code.toLowerCase()):0); // 0 means equal
          activityOpsList = withActivityCodeSorted.map((e: RelationElement) => { return ({ label: `${e.code} - ${e.lookupLabel}`, value: e.lookupId.toString() }) });
          if (extra !=null && extra.value) {
             const found = activityOpsList.find((x:GenListType) => wdpUtil.strEqual(x.value??'',extra.value));
            activityOpsList=found!=null?[found]:[];
          }
        }
      }

      return activityOpsList;
    }

    // Fuel surcharge: resolve amount options by matching equipment specification relation rows.
    if (what === "ratePerUnit4FuelSurcharge") {
      let fuelSurChargeRatesOpsList: GenListType[] = [];
      const targetSpecificationId = (extra != null && extra.EquSpecificationId != null)
        ? extra.EquSpecificationId.toString().trim()
        : null;

      const itemElements = lookups.map((e: LookupRelations) => e.itemElements);
      const withEquForSpecIds = itemElements?.filter((elements: RelationElement[]) =>
        elements.some((n: RelationElement) =>
          wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdEquipmentSpecifications) &&
          (targetSpecificationId == null || (n.specificationId?.toString().trim() ?? "") === targetSpecificationId)
        )
      );

      if (withEquForSpecIds != null && withEquForSpecIds.length > 0) {
        const withFuelRateAmount = withEquForSpecIds
          .map((elements: RelationElement[]) => elements.find((n: RelationElement) => !wdpUtil.strEqual(n.lookupTypeId, domianTableLookupIdEquipmentSpecifications)))
          .filter((e): e is RelationElement => e != null);

        fuelSurChargeRatesOpsList = withFuelRateAmount.map((e: RelationElement) => ({
          label: e.amount?.toString() ?? "",
          value: e.amount?.toString() ?? "",
        }));
        fuelSurChargeRatesOpsList = uniqueByValue(fuelSurChargeRatesOpsList);

        if (extra != null && extra.value) {
          const found = fuelSurChargeRatesOpsList.find((x: GenListType) => wdpUtil.strEqual(x.value ?? '', extra.value));
          fuelSurChargeRatesOpsList = found != null ? [found] : [];
        }
      }

      return fuelSurChargeRatesOpsList;
    }
    
    
    return [];
  }
  static extractFromAttrobute(ticketAtts:string,what:string):string{
    let ret= "";
    if(ticketAtts!=null && ticketAtts!=''){
      try{
        const asJson=JSON.parse(ticketAtts);
        if(asJson!=null){
          if(what==='addOns' && asJson.addOns!=null){
              ret = asJson.addOns;
          }
          if(what==='equipmentId' && asJson.addOns!=null){
              ret = asJson.equipmentId;
          }
        }
      }
      catch(e){
        appLog.warn(e); 
      }
    }
    return ret;
  }
  static packageEquInfo(formValues:TicketInfo_ui):BasicInfo_ui|null{
    if(formValues==null)
      return null;
    return {
        contractNumber: (formValues?.contractNumber as GenListType), 
        serialNumber:   (formValues?.serialNumber as GenListType), 
        unitNumber:     (formValues?.unitNumber as GenListType), 
        equipment:      (formValues?.equipment as GenListType), 
        //addOns:         (formValues?.addOns!=null && (formValues.addOns as GenListType)?.value!=appStrings.noAddOns)? (formValues.addOns as GenListType) : null
        addOns:        (formValues.addOns as GenListType)
    }
  }
  
  
}//class